import {
  createPlaceholderUser,
  userInjectionKey,
  type ID,
  type MutationCallbacks,
  generateVirtualId,
  guest,
} from '#shared'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import { inject, unref } from 'vue'
import { sendMessage } from '../api/send-message'
import type { Message } from '../model/message'
import type { SendMessagePayload } from '../model/send-message-payload'
import type { SendMessageResponse } from '../model/send-message-response'
import { appendMessage } from './append-message'
import { updateMessage } from './update-message'
import { updateMessagesByChatId } from './update-messages-by-chat-id'

type SendMessageMutationCallbacks = MutationCallbacks<SendMessagePayload, SendMessageResponse>

export function useSendMessageMutation(chatId: ID, callbacks: SendMessageMutationCallbacks = {}) {
  const queryClient = useQueryClient()

  const currentUser = inject(userInjectionKey)

  const mutation = useMutation({
    ...callbacks,

    mutationFn: (payload: SendMessagePayload) => sendMessage(chatId, payload),

    onMutate: payload => {
      const currentUserVal = unref(currentUser)
      if (currentUserVal === guest) return

      const optimisticMessageId = generateVirtualId()

      const optimisticAttachments = payload.attachments?.map(attachment => ({
        ...attachment,
        id: generateVirtualId(),
      }))

      const isRespondForMessage = payload.respondFor && 'sender' in payload.respondFor

      const optimisticMessage: Message = {
        id: optimisticMessageId,
        clientId: payload.clientId,
        chatId,
        respondFor: isRespondForMessage ? (payload.respondFor as Message) : null,
        sender: currentUserVal ?? createPlaceholderUser(),
        text: payload.text,
        attachments: optimisticAttachments ?? [],
        reactions: [],
        deletedAt: null,
        createdAt: new Date().toISOString(),
        isBBCode: payload.isBBCode,
        meta: {
          isSending: true,
          isFresh: true,
        },
      }

      if (typeof payload.meta?.resentId === 'number') {
        updateMessage(queryClient, optimisticMessage)
      } else {
        appendMessage(queryClient, optimisticMessage)
      }

      callbacks.onMutate?.(payload)
    },

    onError: (error, payload) => {
      updateMessagesByChatId(queryClient, chatId, message => {
        if (message.clientId !== payload.clientId) return message

        return {
          ...message,
          meta: {
            ...message.meta,
            isSending: false,
            isErrored: true,
            isFresh: true,
          },
        }
      })

      callbacks.onError?.(error, payload)
    },

    onSuccess: (response, payload) => {
      response.data.meta = {
        ...response.data.meta,
        isFresh: true,
      }

      response.data.clientId = payload.clientId

      updateMessage(queryClient, response.data)

      callbacks.onSuccess?.(response, payload)
    },
  })

  return mutation
}
